import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { profileService, PROFILE_QUERY_KEYS } from '../services/profileService';
import { uploadService } from '../services/uploadService';

export const useMyProfile = () => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: PROFILE_QUERY_KEYS.myProfile(),
    queryFn: () => profileService.fetchMyProfile(apiCall),
    enabled: !!isAuthenticated && !!apiCall, 
    staleTime: 5 * 60 * 1000, 
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 3;
    }
  });
};

export const useUserProfile = (userId) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: PROFILE_QUERY_KEYS.profile(userId),
    queryFn: () => profileService.fetchProfile(apiCall, userId),
    enabled: !!isAuthenticated && !!apiCall && !!userId,
    staleTime: 2 * 60 * 1000, 
    cacheTime: 5 * 60 * 1000, 
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 3;
    }
  });
};
export const useUpdateProfile = (options = {}) => {
  const { apiCall } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ changedFields, fullUserData }) => {
      return profileService.updateProfile(apiCall, changedFields);
    },

    retry: false,

    onMutate: async ({ changedFields, fullUserData }) => {
      await queryClient.cancelQueries({ queryKey: PROFILE_QUERY_KEYS.myProfile() });
      const previousProfile = queryClient.getQueryData(PROFILE_QUERY_KEYS.myProfile());

      if (previousProfile && fullUserData) {
        queryClient.setQueryData(PROFILE_QUERY_KEYS.myProfile(), fullUserData);
      }

      if (options.onMutate) {
        options.onMutate();
      }

      return { previousProfile };
    },

    onError: (error, variables, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(PROFILE_QUERY_KEYS.myProfile(), context.previousProfile);
      }
      if (options.onError) {
        options.onError(error);
      }
    },

    onSuccess: (updatedProfile, variables, context) => {
      queryClient.setQueryData(PROFILE_QUERY_KEYS.myProfile(), (oldData) => {
        if (!oldData) return updatedProfile;
        return { ...oldData, ...updatedProfile };
      });

      // Invalidate other profile queries to ensure they refetch
      queryClient.invalidateQueries({ queryKey: ["profile"] });

      if (options.onSuccess) {
        options.onSuccess(updatedProfile);
      }
    }
  });
};

export const useUpdatePassword = (options = {}) => {
  const { apiCall } = useAuth();
  
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }) => {
      return profileService.updatePassword(apiCall, currentPassword, newPassword);
    },
    onSuccess: (data) => {
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    }
  });
};

// Hook to upload profile image
export const useUploadProfileImage = (options = {}) => {
  const { apiCall } = useAuth();
  
  return useMutation({
    mutationFn: (file) => uploadService.uploadProfileImage(file, apiCall),
    
    retry: false,
    
    onSuccess: (imageUrl) => {
      if (options.onSuccess) {
        options.onSuccess(imageUrl);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
  });
};

export const useDeleteProfileImage = (options = {}) => {
  const { apiCall } = useAuth();
  
  return useMutation({
    mutationFn: () => uploadService.deleteProfileImage(apiCall),
    
    retry: false,
    
    onSuccess: () => {
      if (options.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
  });
};

export const useCheckUsername = () => {
  const { apiCall } = useAuth();
  
  return useMutation({
    mutationFn: (username) => profileService.checkUsernameAvailability(apiCall, username),
  });
};

export const usePrefetchUserProfile = () => {
  const { apiCall, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  
  return (userId) => {
    if (!isAuthenticated || !apiCall || !userId) return;
    
    queryClient.prefetchQuery({
      queryKey: PROFILE_QUERY_KEYS.profile(userId),
      queryFn: () => profileService.fetchProfile(apiCall, userId),
      staleTime: 2 * 60 * 1000,
    });
  };
};

export const useProfileByUsername = (username) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['profile', 'username', username],
    queryFn: () => profileService.fetchProfileByUsername(apiCall, username),
    enabled: !!isAuthenticated && !!apiCall && !!username,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404) return false;
      return failureCount < 2;
    }
  });
};

export const useProfileByPostId = (postId) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['profile', 'postId', postId],
    queryFn: () => profileService.fetchProfileByPostId(apiCall, postId),
    enabled: !!isAuthenticated && !!apiCall && !!postId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404) return false;
      return failureCount < 2;
    }
  });
};

export const useSuggestedUsers = (options = {}) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['suggestedUsers'],
    queryFn: () => profileService.fetchSuggestedUsers(apiCall),
    
    enabled: !!isAuthenticated && !!apiCall && (options.enabled !== false),
    
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
  });
};